import {
  ChevronRight,
  X,
  Layers,
  MoreHorizontal,
  Eye,
  EyeOff,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Input } from "@/components/ui/input";
import { TruncatedText } from "components/shared/truncated-text";
import { useEffect, useRef, useState } from "react";
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
  ResponderProvided,
} from "react-beautiful-dnd";
import { ColorPicker } from "../color-picker/color-picker";
import { LayerManager } from "../layer-manager";
import { convertHexToRGBA } from "./layer-utils";
import { LayerConfiguration } from "./models";

interface LayersCardProps {
  layerManager: LayerManager;
  zoomToLayerFn: () => void;
}

export const LayersCard = (props: LayersCardProps) => {
  const [expanded, setExpanded] = useState(false);
  const [expandedLayer, setExpandedLayer] = useState<LayerConfiguration | null>(
    null
  );
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [opacity, setOpacity] = useState(0.5);
  const [thickness, setThickness] = useState(2);
  const [diameter, setDiameter] = useState(2);
  const [fill, setFill] = useState(true);
  const [layerSearchOpen, setLayerSearchOpen] = useState(false);
  const [layerSearchQuery, setLayerSearchQuery] = useState("");
  const [color, setColor] = useState("#FF0000");
  const colorPickerRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [colorPickerTop, setColorPickerTop] = useState(0);
  const [colorPickerRight, setColorPickerRight] = useState(0);

  useEffect(() => {
    if (!colorPickerOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        colorPickerRef.current &&
        !colorPickerRef.current.contains(e.target as Node)
      ) {
        setColorPickerOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [colorPickerOpen]);

  const onDragEnd = (result: DropResult, provided: ResponderProvided) => {
    if (!result.destination) {
      return;
    }
    props.layerManager.reorderLayer(
      result.source.index,
      result.destination.index
    );
  };

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        setColorPickerOpen(false);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const handleChangeColor = (layerId: string, color: string) => {
    props.layerManager.setLayerColour(layerId, color);
    setColor(color);
  };

  const handleChangeOpacity = (layerId: string, opacity: number) => {
    props.layerManager.setLayerOpacity(layerId, opacity);
    setOpacity(opacity);
  };

  const handleChangeThickness = (layerId: string, thickness: number) => {
    props.layerManager.setLayerThickness(layerId, thickness);
    setThickness(thickness);
  };

  const handleChangeDiameter = (layerId: string, diameter: number) => {
    props.layerManager.setLayerDiameter(layerId, diameter);
    setDiameter(diameter);
  };

  const handleChangeFill = (layerId: string, fill: boolean) => {
    props.layerManager.setLayerFill(layerId, fill);
    setFill(fill);
  };

  const selectedLayerNames = props.layerManager.selectedLayers.map(
    (sl) => sl.displayName
  );

  return (
    <>
      {!expanded && (
        <div className="w-10 h-10 flex items-center justify-center self-end rounded-md bg-popover shadow-md">
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => setExpanded(!expanded)}
          >
            <Layers className="size-4" />
          </Button>
        </div>
      )}
      {expanded && (
        <div ref={wrapperRef} className="relative self-end">
        <Card className="w-[290px] max-h-full grow">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Layers</CardTitle>
              <CardDescription>
                Manage visibility, order and colours
              </CardDescription>
            </div>
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => setExpanded(false)}
            >
              <ChevronRight className="size-4" />
            </Button>
          </CardHeader>

          <CardContent className="flex flex-col gap-2">
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="droppable">
                {(provided, snapshot) => (
                  <div
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    className="max-h-[30vh] overflow-y-auto"
                  >
                    <div className="flex flex-col gap-2 mb-5">
                      {props.layerManager.selectedLayers.map(
                        (selectedLayer, index) => (
                          <Draggable
                            key={selectedLayer.id}
                            draggableId={selectedLayer.id}
                            index={index}
                          >
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                              >
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <div
                                      className="h-10 flex items-center justify-between rounded-lg border border-foreground gap-2.5"
                                      style={{
                                        backgroundColor: convertHexToRGBA(
                                          selectedLayer.styles.colour,
                                          selectedLayer.styles.opacity
                                        ),
                                      }}
                                    >
                                      <Button
                                        variant="ghost"
                                        size="icon-sm"
                                        onClick={() =>
                                          props.layerManager.toggleLayerVisibility(
                                            selectedLayer.id
                                          )
                                        }
                                      >
                                        {selectedLayer.visible ? (
                                          <Eye className="size-4" />
                                        ) : (
                                          <EyeOff className="size-4" />
                                        )}
                                      </Button>
                                      <TruncatedText
                                        as="span"
                                        className="w-25 text-xs"
                                      >
                                        {selectedLayer.displayName}
                                      </TruncatedText>
                                      <div className="ml-auto flex">
                                        <Button
                                          variant="ghost"
                                          size="icon-sm"
                                          onClick={(e) => {
                                            if (
                                              colorPickerOpen &&
                                              expandedLayer?.id ===
                                                selectedLayer.id
                                            ) {
                                              setColorPickerOpen(false);
                                            } else {
                                              const btn = e.currentTarget;
                                              const wrapperRect =
                                                wrapperRef.current?.getBoundingClientRect();
                                              const btnRect =
                                                btn.getBoundingClientRect();
                                              if (wrapperRect) {
                                                setColorPickerTop(
                                                  btnRect.bottom -
                                                    wrapperRect.top
                                                );
                                                setColorPickerRight(
                                                  wrapperRect.right -
                                                    btnRect.right
                                                );
                                              }
                                              setExpandedLayer(selectedLayer);
                                              setColorPickerOpen(true);
                                            }
                                          }}
                                        >
                                          <MoreHorizontal className="size-4" />
                                        </Button>

                                        <Button
                                          variant="ghost"
                                          size="icon-sm"
                                          onClick={() =>
                                            props.layerManager.removeLayer(
                                              selectedLayer.id
                                            )
                                          }
                                        >
                                          <X className="size-4" />
                                        </Button>
                                      </div>
                                    </div>
                                  </TooltipTrigger>
                                  <TooltipContent side="left">
                                    {selectedLayer.displayName}
                                  </TooltipContent>
                                </Tooltip>
                              </div>
                            )}
                          </Draggable>
                        )
                      )}
                    </div>
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
            {!layerSearchOpen ? (
              <Button
                variant="outline"
                className="w-full justify-start text-muted-foreground font-normal"
                onClick={() => setLayerSearchOpen(true)}
              >
                Add layer...
              </Button>
            ) : (
              <div className="flex flex-col gap-2 rounded-lg border bg-popover p-2 shadow-md">
                <Input
                  autoFocus
                  placeholder="Search layers..."
                  value={layerSearchQuery}
                  onChange={(e) => setLayerSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") {
                      setLayerSearchOpen(false);
                      setLayerSearchQuery("");
                    }
                  }}
                />
                <div className="max-h-48 overflow-y-auto flex flex-col">
                  {props.layerManager.layers
                    .filter((item) =>
                      item.displayName
                        .toLowerCase()
                        .includes(layerSearchQuery.toLowerCase())
                    )
                    .map((item) => {
                      const isSelected = selectedLayerNames.includes(
                        item.displayName
                      );
                      return (
                        <button
                          key={item.displayName}
                          disabled={isSelected}
                          className="text-left px-2 py-1.5 text-xs rounded-md hover:bg-accent disabled:opacity-50 disabled:pointer-events-none cursor-pointer"
                          onClick={() => {
                            props.layerManager.addLayer(item.displayName);
                            setLayerSearchOpen(false);
                            setLayerSearchQuery("");
                          }}
                        >
                          {item.displayName}
                        </button>
                      );
                    })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
          {colorPickerOpen && expandedLayer && (
            <div
              ref={colorPickerRef}
              className="absolute rounded-lg border bg-popover p-2.5 shadow-md w-[232px] z-10"
              style={{ top: colorPickerTop, right: colorPickerRight }}
            >
              <ColorPicker
                expandedLayer={expandedLayer}
                onChangeColor={handleChangeColor}
                onChangeOpacity={handleChangeOpacity}
                onChangeThickness={handleChangeThickness}
                onChangeDiameter={handleChangeDiameter}
                onChangeFill={handleChangeFill}
                zoomToLayerFn={props.zoomToLayerFn}
              />
            </div>
          )}
        </div>
      )}
    </>
  );
};
